
import { ItemModelType, WorkspaceModelType } from "@stores/WorkspaceStore";
import { updateItemChildIdsRemove } from "./updateItem";
import { _apply_affected_items } from "./_apply_affected_items";
import { deleteDocumentForever } from "../../sync/DocumentApiRequests";
import { ItemType } from "../../API";
import { selectItemPlain } from "../ui/selectItem";
import { logErrorToSentry } from "../../utils/errors/logErrorToSentry";
import { inAppEventsHub } from "../../volatile/InAppEventsHub";
import { removeLocalDoc } from "@stores/document/DocumentLocal";
import { queryGqlApi } from "../../graphql/queryGqlApi";
import { deleteItem as deleteItemGQL } from "../../graphql/mutations";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";
import {AppState} from "@stores/AppState";


export interface DeleteItemDependencies extends CrudServices {
    appState: AppState,
}

/**
 *
 * @param dependencies - Service and store dependencies
 * @param itemId
 * @param workspaceMST
 * @param markedToRemove - will ignore all operations except deletion for the set of items to avoid conflicts
 * @param force
 */
export function deleteItemForever(
    dependencies: DeleteItemDependencies,
    itemId: string,
    workspaceMST: WorkspaceModelType,
    markedToRemove: Set<string> = new Set(),
    force: boolean = false
) {
    if (workspaceMST.selectedId === itemId) {
        selectItemPlain(dependencies.appState.rootStore, workspaceMST, null);
    }
    const itemMST = workspaceMST.items.get(itemId);
    if (!itemMST) {
        return Promise.resolve();
    }
    if (!itemMST!.trashed && !force) {
        console.warn('attempt to delete forever non trashed item');

        return Promise.resolve();
    }
    inAppEventsHub.dispatch({
        type: 'ItemDeleteStart',
        itemId: itemId,
    });
    removeLocalDoc(itemId);
    const onFinish = () => {
        inAppEventsHub.dispatch({
            type: 'ItemDeleteEnd',
            itemId: itemId,
        });
    }

    let waitAffected: Promise<any> = _apply_affected_items(
        workspaceMST,
        itemMST,
        async (item) => {
            // otherwise we delete the child too
            // @ts-ignore
            await deleteItemForever(dependencies, item.id, workspaceMST);
        },
        async (item) => {
            // if the child of deleted element has other parents we clear deleted element from the child parent list
            if (markedToRemove.has(item.id)) {
                /* if we are going to delete the parent (can be caused by empty trash and multi parent cluster combination)
                changing childIds fields on it first - will lead to conflict upon deletion */
                return Promise.resolve();
            }
            // @ts-ignore
            await updateItemChildIdsRemove(itemId, item.id, workspaceMST);
        },
        async (item) => {
            if (markedToRemove.has(item.id)) {
                /* if we are going to delete the parent (can be caused by empty trash and multi parent cluster combination)
                changing childIds fields on it first - will lead to conflict upon deletion */
                return Promise.resolve();
            }
            // if deleted item was some other item's child we clear that connection on parent item's childIds,
            // but we leave our deleted target parentIds intact to be able to restore to the same places
            // @ts-ignore
            await updateItemChildIdsRemove(item.id, itemId, workspaceMST);
        },
    ).catch(logErrorToSentry);

    waitAffected = waitAffected.then(() => {
        return Promise.all([
            workspaceMST.removeItem(itemId),
            deleteItemFromDB(dependencies, itemMST),
        ]).then(onFinish);
    });

    return waitAffected;
}

async function deleteItemFromDB(dependencies: DeleteItemDependencies, targetItem: ItemModelType) {
    const { authService, dataClientService, appState } = dependencies;
    
    if (targetItem.type === ItemType.LEAF) {
        try {
            await deleteDocumentForever(appState.userState!.wsServerConnection, targetItem.id);
        } catch(error: any) {
            if (error.s === 404 || error.message === 'Not found') {
                // ignore error if document already deleted
            } else {
                throw error;
            }
        }
    }

    const itemsDs = appState.userState!.dsManager!.getItemsDataSource(targetItem.workspaceId);
    const currentItemDB = itemsDs.allRows.find(itemRow => itemRow.id === targetItem.id);

    /* @ts-ignore ReadOnly fields have type issue on amplify  */
    await queryGqlApi(
        deleteItemGQL,
        {
            input: {
                id: targetItem.id,
                _version: currentItemDB!._version,
            }
        },
        dataClientService,
        authService,
        'main'
    )
}
